import React from 'react';
import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { BuyerCode, Code, Counter, IconBackground, Image, Label } from 'common/components';
import { EColor } from 'common/const';
import { getEmail, getPriceToDisplay, mapToMenuWithDivider, phoneFormatter, priceFormatter } from 'common/helpers';
import { DEFAULT_GOODS_COUNT } from 'common/config';
import { Affiliate, ArrowRightLong, Cart } from 'common/svg';
import { IAccountShort, IOption, ISubdivisionShort } from 'common/models';
import { getIsToday, getIsTomorrow, getToday, getTomorrow } from 'common/helpers/date.helper';
import { deliveryTimeFormatter } from 'common/helpers/formatter.helper';
import {
  EDeliveryType,
  EPickUpDate,
  IBasket,
  IBasketDelivery,
  IBasketGoods,
  IBasketItem,
  IBasketStore,
} from 'entities/Basket/Basket.models';
import { IGood } from 'entities/Goods/Goods.models';
import { getSubdivisionOptions } from 'entities/Subdivision/Subdivision.helper';
import { ISubdivisionAddress } from 'entities/Subdivision/Subdivision.models';
import { ILegal } from 'entities/LegalEntity/LegalEntity.models';
import { getLegalEntityOptions } from 'entities/LegalEntity/LegalEntity.helper';
import { getUserName } from 'entities/User/User.helper';
import { dayOrder, IStockSchedule } from 'entities/Stock/Stock.models';

const dayNames = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

export const getBasketProgressColor = (percent: number) => {
  if (percent >= 95) {
    return '#EC6665';
  } else if (percent >= 60) {
    return '#F6C543';
  } else {
    return '#69A443';
  }
};

export const renderBasketRecords = ({
  account,
  onCountChange,
}: {
  account?: IAccountShort;
  onCountChange: (id: number, value: number) => void;
}): ColumnsType<IBasketGoods> => {
  return [
    {
      key: 'image',
      render: (_, { goodId, image }) => {
        return (
          <a href={`/good/${goodId}`} target="_blank" rel="noopener noreferrer">
            <Image size={104} src={image} />
          </a>
        );
      },
      width: 144,
    },
    {
      key: 'name',
      render: (_, { goodId, name, sellerCode, buyerCode }) => {
        return (
          <a href={`/good/${goodId}`} target="_blank" rel="noopener noreferrer">
            <div className="mb-4 ff-medium fs-m color-black ellipsis-2">{name}</div>

            <div className="mb-6 flex align-center gap-4">
              <Code sellerCode={sellerCode} />

              <BuyerCode code={buyerCode} />
            </div>
          </a>
        );
      },
    },
    {
      key: 'price',
      render: (_, { count, price, priceWithTaxes }) => {
        const priceToDisplay = getPriceToDisplay(price, priceWithTaxes, account);

        return (
          <div>
            <div className="mb-2 ff-semibold fs-m text-nowrap">{priceFormatter(count * priceToDisplay)}</div>

            {count > DEFAULT_GOODS_COUNT && (
              <span className="ff-regular fs-xs text-nowrap color-dark-grey">{`${priceFormatter(priceToDisplay)}/шт`}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'count',
      render: (_, { goodId, count }) => {
        return <Counter count={count} responsive onChange={(value) => onCountChange(goodId, value)} />;
      },
    },
  ];
};

export const mapBasketGoodsToPayload = (goods: IBasketGoods[]) =>
  goods.map((item) => ({ goodId: item.goodId, count: item.count }));

export const filterBasketGoods = {
  byId: (goods: IBasketGoods[], id: number) => goods.filter((item) => item.goodId === id),
  notEqualId: (goods: IBasketGoods[], id: number) => goods.filter((item) => item.goodId !== id),
  getAvailableForCustomerGoods: (goods: IBasketGoods[], maxGoodPrice?: number) => {
    if (maxGoodPrice !== undefined) {
      return goods.filter((item) => item.price < maxGoodPrice && item.isAvailableForCustomer);
    }

    return goods.filter((item) => item.isAvailableForCustomer);
  },
};

export const getBasketGoodsDataSource = (list: IBasketGoods[]) => list.map((goods) => ({ key: goods.goodId, ...goods }));

export const getBasketGoodsRowKeys = (list: IBasketGoods[]) => list.map((goods) => goods.goodId);

export const getBasketGeneralInformation = ({
  goods,
  basket,
  delivery,
}: {
  goods: IBasketGoods[];
  basket: IBasket | null;
  delivery?: IBasketDelivery;
}): {
  count: number;
  amount: number;
  totalAmount: number;
  vatAmount: number;
  deliveryAmount: number;
  benefit: number;
  availableAmount: number;
  isLimitExceeded: boolean;
} => {
  const { isDelivery } = getBasketDeliveryType(basket);
  const count = goods.reduce((acc, item) => acc + item.count, 0);
  const amount = goods.reduce((acc, item) => acc + item.count * item.price, 0);
  let totalAmount = goods.reduce((acc, item) => acc + item.count * item.priceWithTaxes, 0);
  let vatAmount = totalAmount - amount;
  const deliveryAmount = delivery?.deliveryPrice ?? 0;
  const benefit = goods.reduce((acc, item) => {
    const priceToDisplay = getPriceToDisplay(item.price, item.priceWithTaxes, basket?.account);
    const originalPriceToDisplay = getPriceToDisplay(item.originalPrice, item.originalPriceWithTaxes, basket?.account);

    return acc + item.count * (originalPriceToDisplay - priceToDisplay);
  }, 0);
  const availableAmount = basket?.availableLimit !== undefined ? basket.availableLimit - amount : 0;
  const isLimitExceeded = basket?.availableLimit !== undefined ? availableAmount < 0 : false;

  if (isDelivery) {
    totalAmount += delivery?.deliveryPriceWithTaxes ?? 0;
    vatAmount += (delivery?.deliveryPriceWithTaxes ?? 0) - deliveryAmount;
  }

  return {
    count,
    amount,
    totalAmount,
    vatAmount,
    deliveryAmount,
    benefit,
    availableAmount,
    isLimitExceeded,
  };
};

export const getPickUpStoresCounts = (stores: IBasketStore[]): { todayCount: number; tomorrowCount: number } => {
  return stores.reduce(
    (acc, item) => {
      if (item.pickUpDate === EPickUpDate.Today) {
        acc.todayCount++;
        acc.tomorrowCount++;
      }

      if (item.pickUpDate === EPickUpDate.Tomorrow) {
        acc.tomorrowCount++;
      }

      return acc;
    },
    { todayCount: 0, tomorrowCount: 0 },
  );
};

export const getPickUpDateOptions = (stores: IBasketStore[]): IOption[] => {
  const today = getToday().format('DD MMMM');
  const tomorrow = getTomorrow().format('DD MMMM');
  const { todayCount, tomorrowCount } = getPickUpStoresCounts(stores);

  return [
    { value: EPickUpDate.Today, label: `Сегодня, ${today}`, disabled: !todayCount },
    { value: EPickUpDate.Tomorrow, label: `Завтра, ${tomorrow}`, disabled: !tomorrowCount },
  ];
};

export const getDefaultPickUpDateValue = (stores: IBasketStore[]): EPickUpDate | null => {
  const { todayCount, tomorrowCount } = getPickUpStoresCounts(stores);

  switch (true) {
    case !!todayCount: {
      return EPickUpDate.Today;
    }
    case !!tomorrowCount: {
      return EPickUpDate.Tomorrow;
    }
    default: {
      return null;
    }
  }
};

const checkPickupDateMatchesStoreDate = (pickUpDate: EPickUpDate | null, store: IBasketStore): boolean => {
  if (!pickUpDate || pickUpDate === EPickUpDate.ManagerWillConfirm) {
    return store.pickUpDate === EPickUpDate.ManagerWillConfirm;
  }

  if (pickUpDate === EPickUpDate.Today) {
    return store.pickUpDate === EPickUpDate.Today;
  }

  return store.pickUpDate === EPickUpDate.Today || store.pickUpDate === EPickUpDate.Tomorrow;
};

export const filterPickUpStores = (stores: IBasketStore[], search: string, pickUpDate: EPickUpDate | null): IBasketStore[] => {
  return stores.filter((item) => {
    const matchBySearch = item.address.toLowerCase().includes(search.toLowerCase());
    const isPickupDateMatchingStoreDate = checkPickupDateMatchesStoreDate(pickUpDate, item);

    return matchBySearch && isPickupDateMatchingStoreDate;
  });
};

export const getDeliveryDateByPickUpDate = (pickUpDate: EPickUpDate | null): string | undefined => {
  switch (pickUpDate) {
    case EPickUpDate.Today:
      return getToday().format('YYYY-MM-DD');
    case EPickUpDate.Tomorrow:
      return getTomorrow().format('YYYY-MM-DD');
    default:
      return undefined;
  }
};

export const translatePickUpDate = (value: string): string => {
  const tomorrow = getTomorrow().format('DD MMMM');

  switch (value) {
    case EPickUpDate.Tomorrow: {
      return `Будет готов завтра, ${tomorrow}`;
    }
    case EPickUpDate.ManagerWillConfirm: {
      return 'Менеджер уточнит дату поставки';
    }
    default: {
      return 'Можно забрать сегодня';
    }
  }
};

export const mapGoodToBasketGood = (goods: IGood): IBasketGoods => ({
  goodId: goods.id,
  count: DEFAULT_GOODS_COUNT,
  brand: goods.brand,
  image: goods.images?.[0],
  price: goods.price,
  priceWithTaxes: goods.priceWithTaxes,
  originalPrice: goods.originalPrice,
  originalPriceWithTaxes: goods.originalPriceWithTaxes,
  name: goods.name,
  externalId: goods.externalId,
  buyerCode: goods.buyerCode,
  sellerCode: goods.sellerCode,
  properties: goods.properties,
  remains: goods.remains,
  tnvd: goods.tnvd,
  isAvailableForCustomer: goods.isAvailableForCustomer,
});

export const getBasketSubdivisionOptions = (list: ISubdivisionShort[]) => {
  const options = getSubdivisionOptions(list);
  return mapToMenuWithDivider(options);
};

export const getBasketAddressOptions = (list: ISubdivisionAddress[] = [], selectedAddressId: number | null) => {
  const options = list.map((address) => ({
    label: (
      <div>
        <div className={`fs-m ${address.id === selectedAddressId ? 'ff-medium' : 'ff-regular'}`}>{address.name}</div>

        {address.isMain && <div className="mt-3 ff-regular fs-xs color-dark-grey">Основной адрес</div>}
      </div>
    ),
    value: address.id,
    desc: address.name,
  }));

  return mapToMenuWithDivider(options);
};

export const getBasketLegalEntityOptions = (list: ILegal[]) => {
  const options = getLegalEntityOptions(list);
  return mapToMenuWithDivider(options);
};

export const renderActiveBasketListRecords = (navigateToBasket: (id: number) => void) => {
  return [
    {
      key: 'id',
      render: (_, { id }) => (
        <div className="flex align-center text-nowrap">
          <div className="mr-6">
            <IconBackground content={<Cart className="icon-cart-bright-green" />} color={EColor.PaleGreen} />
          </div>

          <div className="ff-medium fs-s">{`#${id}`}</div>
        </div>
      ),
      width: 100,
    },
    {
      key: 'userName',
      render: (_, { user }) => {
        return <span className="ff-medium fs-s">{getUserName.fullName(user.lastName, user.firstName, user.patronymic)}</span>;
      },
    },
    {
      key: 'email',
      render: (_, { user }) => {
        return <span className="ff-regular fs-s color-dark-grey">{getEmail(user.email)}</span>;
      },
    },
    {
      key: 'phone',
      render: (_, { user }) => {
        return <span className="ff-regular fs-s color-dark-grey">{phoneFormatter(user.phone)}</span>;
      },
    },
    {
      key: 'company',
      render: (_, { account: { name } }) => {
        return <Label icon={<Affiliate className="icon-affiliate-dark-grey" />} name={name} color={EColor.LightGrey} />;
      },
    },
    {
      key: 'subdivision',
      render: (_, { subdivision }) => {
        return subdivision ? <span className="ff-regular fs-s">{subdivision.name}</span> : null;
      },
    },
    {
      key: 'action',
      render: (_, { id }) => {
        return <Button className="button-circle secondary" icon={<ArrowRightLong />} onClick={() => navigateToBasket(id)} />;
      },
      width: 64,
    },
  ] as ColumnsType<IBasketItem>;
};

const splitToRanges = (days: number[]): number[][] => {
  const ranges: number[][] = [];
  let current: number[] = [days[0]];

  for (let i = 1; i < days.length; i++) {
    if (days[i] === days[i - 1] + 1) {
      current.push(days[i]);
    } else {
      ranges.push(current);
      current = [days[i]];
    }
  }

  ranges.push(current);

  return ranges;
};

const formatRange = (range: number[]): string => {
  if (range.length === 1) {
    return dayNames[range[0] - 1];
  }

  return `${dayNames[range[0] - 1]}–${dayNames[range[range.length - 1] - 1]}`;
};

export const formatBasketStoreSchedules = (schedules?: IStockSchedule[]): string[] => {
  if (!schedules) {
    return [];
  }

  const workGroups = new Map<string, number[]>();
  const offDays: number[] = [];

  schedules.forEach((item) => {
    const dayNumber = dayOrder[item.dayOfWeek];

    if (item.isDayOff) {
      offDays.push(dayNumber);
      return;
    }

    const key = `${item.openTime}-${item.closeTime}`;

    if (!workGroups.has(key)) {
      workGroups.set(key, []);
    }

    workGroups.get(key)?.push(dayNumber);
  });

  const sortedWorkGroups = [...workGroups.entries()].sort((a, b) => Math.min(...a[1]) - Math.min(...b[1]));

  const result: string[] = [];

  for (const [key, days] of sortedWorkGroups) {
    const sortedDays = [...days].sort((a, b) => a - b);
    const ranges = splitToRanges(sortedDays).map(formatRange).join(', ');
    const [open, close] = key.split('-');

    result.push(`${ranges} ${open}—${close}`);
  }

  if (offDays.length) {
    const sortedOffDays = offDays.sort((a, b) => a - b);
    const ranges = splitToRanges(sortedOffDays).map(formatRange).join(', ');

    result.push(`${ranges} Выходной`);
  }

  return result;
};

export const getBasketDeliveryType = (basket: IBasket | null) => {
  return {
    isPickUp: basket?.deliveryType === EDeliveryType.PickUp,
    isDelivery: basket?.deliveryType === EDeliveryType.Delivery,
  };
};

export const isDateBeforeDeliveryStartDate = (current: dayjs.Dayjs, deliveryInDays: number): boolean => {
  // Первый доступный день — по бакету подписи срока («72 часа» → +3, «1 неделя» → +7, ...),
  // а не по сырому deliveryInDays — календарь согласован с тем, что видит пользователь.
  const bucketDays = deliveryTimeFormatter.bucketDays(deliveryInDays);

  if (bucketDays === null) {
    // «Доступно под заказ» — дата не выбирается.
    return true;
  }

  return current < dayjs().add(bucketDays, 'day').startOf('day');
};

export const getIsDeliveryDateUnavailable = (deliveryInDays: number | undefined, deliveryDate: string | undefined): boolean => {
  if (deliveryInDays === undefined) {
    return true;
  }

  if (!deliveryDate) {
    return true;
  }

  const bucketDays = deliveryTimeFormatter.bucketDays(deliveryInDays);

  if (bucketDays === null) {
    return true;
  }

  return dayjs(deliveryDate).isBefore(dayjs().add(bucketDays, 'day'), 'day');
};

export const validateBasketDelivery = (
  basket: IBasket | null,
  stores: IBasketStore[],
  deliveryInDays: number | undefined,
): string[] => {
  const storeError = 'Доступные магазины самовывоза для заказа изменились.';
  const deliveryDateError = 'Доступные даты получения заказа изменились.';

  const errors: string[] = [];

  if (!basket?.deliveryType) {
    return errors;
  }

  const { isPickUp, isDelivery } = getBasketDeliveryType(basket);

  if (isPickUp) {
    const store = stores.find((item) => item.id === basket.storeId);

    if (!store) {
      errors.push(storeError);
    }

    const isToday = getIsToday(basket.deliveryDate);
    const isTomorrow = getIsTomorrow(basket.deliveryDate);

    if (isToday && store?.pickUpDate !== EPickUpDate.Today) {
      errors.push(deliveryDateError);
    }

    if (isTomorrow && store?.pickUpDate !== EPickUpDate.Today && store?.pickUpDate !== EPickUpDate.Tomorrow) {
      errors.push(deliveryDateError);
    }

    const availablePickUpDates = stores.map((item) => item.pickUpDate);

    if (isToday && !availablePickUpDates.includes(EPickUpDate.Today)) {
      errors.push(deliveryDateError);
    }

    if (isTomorrow && !availablePickUpDates.includes(EPickUpDate.Today) && !availablePickUpDates.includes(EPickUpDate.Tomorrow)) {
      errors.push(deliveryDateError);
    }
  }

  if (isDelivery && basket.deliveryDate) {
    const isDeliveryDateUnavailable = getIsDeliveryDateUnavailable(deliveryInDays, basket.deliveryDate);

    if (isDeliveryDateUnavailable) {
      errors.push(deliveryDateError);
    }
  }

  return [...new Set(errors)];
};

export const updateBasketGoodCount = (goods: IBasketGoods[], id: number, count: number): IBasketGoods[] => {
  return goods.map((item) => (item.goodId === id ? { ...item, count } : item));
};
